import React, { useEffect, useState } from 'react';
import BrandLogo from './BrandLogo';
import ClinicForm from '../components/Form/TuMesaYaForm';
import dentalTreatment from '../assets/dental-treatment.webp';
import { FormData } from '../types';
import { submitLeadToWebhook } from '../utils/api';
import { useTracking } from '../hooks/useTracking';
import { LOGIN_URL, REGISTER_URL } from '../config/appLinks';

type Channel = 'phone' | 'whatsapp';
type ReminderState = 'pending' | 'confirmed' | 'cancelled';
type SubmitState = 'idle' | 'success' | 'error';

const PhoneConversation: React.FC = () => (
  <div className="conversation-panel conversation-panel--phone">
    <div className="conversation-status">
      <span className="live-dot" />
      Llamada en curso · 00:42
    </div>
    <div className="transcript">
      <p>
        <small>Paciente</small>
        Quería pedir cita para una limpieza dental.
      </p>
      <p className="transcript__agent">
        <small>CitasClinicas.AI</small>
        Claro. Tengo disponibilidad el martes a las 18:30 o el jueves a las
        10:00.
      </p>
      <p>
        <small>Paciente</small>
        El martes me viene perfecto.
      </p>
    </div>
    <div className="appointment-confirmation">
      <span>✓</span>
      <div>
        <small>Cita confirmada</small>
        <strong>Martes · 18:30 · Higiene dental</strong>
      </div>
    </div>
  </div>
);

const WhatsAppConversation: React.FC = () => (
  <div className="conversation-panel conversation-panel--whatsapp">
    <div className="conversation-status">
      <span className="wa-dot">WA</span>
      Conversación por WhatsApp
      <span className="tab-soon">Próximamente</span>
    </div>
    <div className="wa-bubble wa-bubble--patient">
      Hola, necesito una primera valoración de ortodoncia.
    </div>
    <div className="wa-bubble wa-bubble--agent">
      Perfecto. ¿Te viene bien el miércoles a las 17:00?
      <small>12:31 ✓✓</small>
    </div>
    <div className="wa-bubble wa-bubble--patient">Sí, gracias.</div>
    <div className="appointment-confirmation">
      <span>✓</span>
      <div>
        <small>Cita confirmada</small>
        <strong>Miércoles · 17:00 · Ortodoncia</strong>
      </div>
    </div>
  </div>
);

const LandingPage: React.FC = () => {
  const [activeChannel, setActiveChannel] = useState<Channel>('phone');
  const [reminderState, setReminderState] = useState<ReminderState>('pending');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [hideStickyCta, setHideStickyCta] = useState(false);
  const { trackCTAClick, trackPageView } = useTracking();

  useEffect(() => {
    trackPageView('tuclinicaya-dental', 'organic');

    const revealObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    document.querySelectorAll('[data-reveal]').forEach(element => {
      revealObserver.observe(element);
    });

    const demo = document.getElementById('demo');
    const stickyObserver = new IntersectionObserver(
      entries => setHideStickyCta(entries.some(entry => entry.isIntersecting)),
      { threshold: 0.08 }
    );

    if (demo) stickyObserver.observe(demo);

    return () => {
      revealObserver.disconnect();
      stickyObserver.disconnect();
    };
  }, [trackPageView]);

  const handleCta = (source: string) => {
    trackCTAClick('solicitar-demo', source);
  };

  const handleFormSubmit = async (formData: FormData) => {
    setIsSubmitting(true);
    setSubmitState('idle');

    const response = await submitLeadToWebhook({
      ...formData,
      clinic: formData.restaurant,
      brand: 'CitasClinicas.AI',
      vertical: 'dental',
      source: 'tuclinicaya-dental-demo',
      timestamp: new Date().toISOString(),
    });

    setIsSubmitting(false);
    setSubmitState(response.success ? 'success' : 'error');
    return response.success;
  };

  return (
    <div className="clinic-site">
      <header className="clinic-header">
        <a href="#inicio" className="clinic-header__logo">
          <BrandLogo compact />
        </a>
        <nav aria-label="Navegación principal">
          <a href="#como-funciona">Cómo funciona</a>
          <a href="#menos-ausencias">Menos ausencias</a>
        </nav>
        <div className="clinic-header__actions">
          <a
            className="clinic-header__login"
            href={LOGIN_URL}
            onClick={() => handleCta('header-login')}
          >
            Acceder
          </a>
          <a
            href={REGISTER_URL}
            className="button button--small"
            onClick={() => handleCta('header-alta')}
          >
            Crear cuenta
          </a>
        </div>
      </header>

      <main>
        <section className="clinic-hero" id="inicio">
          <div className="clinic-hero__copy" data-reveal>
            <span className="availability-pill">
              <span className="live-dot" /> Recepción activa · 24/7
            </span>
            <h1>
              Tu gabinete está ocupado. <em>Tu recepción sigue atendiendo.</em>
            </h1>
            <p>
              CitasClinicas.AI gestiona citas por teléfono, consulta la
              disponibilidad y confirma cada visita incluso cuando la clínica
              está cerrada. El canal de WhatsApp llegará muy pronto: todavía no
              está disponible.
            </p>
            <div className="hero-actions">
              <a
                href={REGISTER_URL}
                className="button"
                onClick={() => handleCta('hero-alta')}
              >
                Crear mi cuenta
                <span aria-hidden="true">↗</span>
              </a>
              <a
                href="#demo"
                className="text-link"
                onClick={() => handleCta('hero-demo')}
              >
                Prefiero ver una demo
              </a>
            </div>
            <ul className="hero-proof">
              <li>
                <strong>1</strong>
                <span>canal conectado</span>
                <span className="tab-soon">WhatsApp pronto</span>
              </li>
              <li>
                <strong>24/7</strong>
                <span>recepción disponible</span>
              </li>
              <li>
                <strong>1</strong>
                <span>agenda actualizada</span>
              </li>
            </ul>
          </div>

          <div className="reception-console" data-reveal>
            <div className="console-topbar">
              <BrandLogo compact />
              <span className="console-live">
                <span className="live-dot" /> En directo
              </span>
            </div>
            <div className="incoming-card">
              <span className="incoming-card__icon">☎</span>
              <div>
                <small>Nueva llamada</small>
                <strong>Paciente solicita una cita</strong>
              </div>
              <span className="sound-bars" aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
              </span>
            </div>
            <div className="console-thread">
              <p>
                <small>Paciente</small>
                “Necesito una limpieza dental por la tarde.”
              </p>
              <p>
                <small>CitasClinicas.AI</small>
                “Tengo un hueco el martes a las 18:30.”
              </p>
            </div>
            <div className="console-result">
              <span>✓</span>
              <div>
                <small>Agenda actualizada</small>
                <strong>Martes · 18:30 · Higiene</strong>
              </div>
            </div>
            <div className="console-footer">
              <span>Teléfono</span>
              <span className="tab-soon">WhatsApp · Próximamente</span>
              <span>Recordatorios</span>
            </div>
          </div>
        </section>

        <section className="trust-ribbon" aria-label="Funciones principales">
          {/* La píldora va primera: en móvil la cinta se recorta por la derecha. */}
          <span className="tab-soon">WhatsApp · Próximamente</span>
          <span>Recepción por teléfono</span>
          <span>Citas fuera de horario</span>
          <span>Confirmaciones automáticas</span>
          <span>Cancelaciones que liberan agenda</span>
        </section>

        <section className="appointment-section" id="como-funciona">
          <div className="section-heading" data-reveal>
            <span className="section-label">01 · UNA SOLA RECEPCIÓN</span>
            <h2>
              Una cita empieza con una llamada. Muy pronto, también con un
              mensaje.
            </h2>
            <p>
              CitasClinicas.AI entiende qué necesita el paciente, consulta los
              horarios disponibles y deja la cita registrada sin interrumpir al
              equipo. Hoy funciona por teléfono; el canal de WhatsApp está en
              preparación.
            </p>
          </div>
          <div className="channel-demo" data-reveal>
            <div className="channel-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeChannel === 'phone'}
                className={activeChannel === 'phone' ? 'is-active' : ''}
                onClick={() => setActiveChannel('phone')}
              >
                <span>☎</span> Llamada
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeChannel === 'whatsapp'}
                className={activeChannel === 'whatsapp' ? 'is-active' : ''}
                onClick={() => setActiveChannel('whatsapp')}
              >
                <span className="wa-dot">WA</span> WhatsApp
                <span className="tab-soon">Próximamente</span>
              </button>
            </div>
            {activeChannel === 'phone' ? (
              <PhoneConversation />
            ) : (
              <div className="coming-soon">
                <p className="coming-soon__band">Próximamente</p>
                <div
                  className="coming-soon__preview"
                  role="group"
                  aria-label="Simulación: el canal de WhatsApp aún no está disponible"
                >
                  <WhatsAppConversation />
                </div>
                <p className="coming-soon__note">
                  Esto es una simulación. La atención por WhatsApp todavía no
                  está disponible.
                </p>
              </div>
            )}
          </div>
          <ol className="appointment-steps">
            <li data-reveal>
              <span>1</span>
              <div>
                <strong>Entiende la necesidad</strong>
                <p>Primera visita, higiene, ortodoncia o revisión.</p>
              </div>
            </li>
            <li data-reveal>
              <span>2</span>
              <div>
                <strong>Consulta la disponibilidad</strong>
                <p>Según servicio, duración, profesional y horario.</p>
              </div>
            </li>
            <li data-reveal>
              <span>3</span>
              <div>
                <strong>Confirma y registra</strong>
                <p>El paciente recibe los datos y la agenda queda al día.</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="treatment-section">
          <div className="treatment-visual" data-reveal>
            <img
              src={dentalTreatment}
              alt="Odontóloga y auxiliar atendiendo a un paciente durante un tratamiento"
            />
            <div className="handled-call">
              <span className="handled-call__pulse">☎</span>
              <div>
                <small>Llamada entrante</small>
                <strong>CitasClinicas.AI la está atendiendo</strong>
              </div>
            </div>
          </div>
          <div className="treatment-copy" data-reveal>
            <span className="section-label">02 · DURANTE LA CONSULTA</span>
            <h2>Una cita no debería interrumpir otra cita.</h2>
            <p>
              La odontóloga, el higienista y la recepción pueden concentrarse en
              la persona que ya está en la clínica. Mientras tanto, cada nueva
              consulta recibe respuesta.
            </p>
            <ul className="check-list">
              <li>El paciente que llama no espera ni abandona.</li>
              <li>El equipo no sale del gabinete para coger el teléfono.</li>
              <li>La agenda se actualiza durante la conversación.</li>
            </ul>
          </div>
        </section>

        <section className="after-hours-section">
          <div className="after-hours-copy" data-reveal>
            <span className="section-label section-label--light">
              03 · FUERA DE HORARIO
            </span>
            <h2>La clínica cierra. La recepción digital no.</h2>
            <p>
              Por la noche, durante el fin de semana o en vacaciones, los
              pacientes siguen pudiendo consultar horarios y reservar una cita.
            </p>
          </div>
          <div className="night-agenda" data-reveal>
            <div className="night-clock" aria-hidden="true">
              <span className="clock-hand clock-hand--hour" />
              <span className="clock-hand clock-hand--minute" />
              <i />
            </div>
            <div className="night-event">
              <small>Domingo · 22:47</small>
              <strong>Nueva cita confirmada</strong>
              <span>Primera visita · Jueves 10:30</span>
            </div>
            <div className="night-event night-event--wa coming-soon">
              <p className="coming-soon__band coming-soon__band--corner">
                Próximamente
              </p>
              <div
                className="coming-soon__preview"
                role="group"
                aria-label="Simulación: el canal de WhatsApp aún no está disponible"
              >
                <small>WhatsApp · 23:02</small>
                <strong>Horario de la clínica consultado</strong>
                <span>Respuesta enviada automáticamente</span>
              </div>
              <p className="coming-soon__note coming-soon__note--light">
                Ejemplo de lo que hará WhatsApp cuando esté disponible.
              </p>
            </div>
          </div>
        </section>

        <section className="reminder-section" id="menos-ausencias">
          <div className="reminder-copy" data-reveal>
            <span className="section-label">04 · ANTES DE LA CITA</span>
            <h2>
              Menos ausencias. Menos huecos que se descubren demasiado tarde.
            </h2>
            <p>
              CitasClinicas.AI enviará recordatorios por WhatsApp. El paciente
              podrá confirmar o cancelar, y la agenda quedará actualizada en el
              momento. Es una función en preparación:{' '}
              <strong>WhatsApp todavía no está disponible</strong>.
            </p>
            <div className="reminder-flow">
              <span>Recordatorio</span>
              <i>→</i>
              <span>Respuesta</span>
              <i>→</i>
              <span>Agenda</span>
              <span className="tab-soon">Próximamente</span>
            </div>
          </div>
          <div className="reminder-phone coming-soon" data-reveal>
            <p className="coming-soon__band">Próximamente</p>
            <div
              className="coming-soon__preview"
              role="group"
              aria-label="Simulación: el recordatorio por WhatsApp aún no está disponible"
            >
              <div className="reminder-phone__header">
                <span className="wa-dot">WA</span>
                <div>
                  <strong>CitasClinicas.AI</strong>
                  <small>Recordatorio de cita · Próximamente</small>
                </div>
              </div>
              <div className="reminder-bubble">
                <p>
                  Hola, Marta. Te recordamos tu cita de higiene dental mañana a
                  las 18:30.
                </p>
                {reminderState === 'pending' && (
                  <div className="reminder-actions">
                    <button
                      type="button"
                      onClick={() => setReminderState('confirmed')}
                    >
                      ✓ Confirmar
                    </button>
                    <button
                      type="button"
                      onClick={() => setReminderState('cancelled')}
                    >
                      Cancelar cita
                    </button>
                  </div>
                )}
                <small>18:00 ✓✓</small>
              </div>
              <div aria-live="polite">
                {reminderState === 'confirmed' && (
                  <div className="reminder-result reminder-result--confirmed">
                    <span>✓</span>
                    <div>
                      <strong>Cita confirmada</strong>
                      <small>La agenda ya está actualizada</small>
                    </div>
                  </div>
                )}
                {reminderState === 'cancelled' && (
                  <div className="reminder-result">
                    <span>↺</span>
                    <div>
                      <strong>Cancelación recibida</strong>
                      <small>Hueco disponible de nuevo</small>
                    </div>
                  </div>
                )}
                {reminderState !== 'pending' && (
                  <button
                    type="button"
                    className="reset-demo"
                    onClick={() => setReminderState('pending')}
                  >
                    Repetir simulación
                  </button>
                )}
              </div>
            </div>
            <p className="coming-soon__note">
              Simulación de cómo funcionará. Los recordatorios por WhatsApp
              todavía no están disponibles.
            </p>
          </div>
        </section>

        <section className="clinic-control-section">
          <div className="section-heading section-heading--center" data-reveal>
            <span className="section-label">05 · TODO EN SU SITIO</span>
            <h2>
              Cada tratamiento tiene su tiempo. Cada profesional, su agenda.
            </h2>
            <p>
              La conversación se adapta a la operativa de la clínica sin
              convertir al paciente en un formulario.
            </p>
          </div>
          <div className="control-cards">
            <article data-reveal>
              <span className="control-index">01</span>
              <strong>Servicios y duraciones</strong>
              <p>
                Una higiene no ocupa lo mismo que una primera valoración o una
                revisión.
              </p>
            </article>
            <article data-reveal>
              <span className="control-index">02</span>
              <strong>Profesionales</strong>
              <p>
                Ofrece citas según el odontólogo, higienista o especialista
                adecuado.
              </p>
            </article>
            <article data-reveal>
              <span className="control-index">03</span>
              <strong>Información de la clínica</strong>
              <p>
                Responde preguntas habituales sobre horarios, ubicación y
                preparación de la visita.
              </p>
            </article>
          </div>
        </section>

        <section className="trust-section">
          <div className="trust-symbol" aria-hidden="true">
            <span />
          </div>
          <div data-reveal>
            <span className="section-label">06 · CON LÍMITES CLAROS</span>
            <h2>
              Recepción inteligente. Decisiones clínicas, siempre humanas.
            </h2>
            <p>
              CitasClinicas.AI organiza citas e información administrativa. No
              diagnostica, no recomienda tratamientos y nunca sustituye el
              criterio del equipo sanitario.
            </p>
          </div>
        </section>

        <section className="demo-section" id="demo">
          <div className="demo-copy" data-reveal>
            <span className="section-label section-label--light">
              SOLICITA UNA DEMO
            </span>
            <h2>
              Escucha cómo atendería CitasClinicas.AI a los pacientes de tu clínica.
            </h2>
            <p>
              Prepararemos un recorrido con tus tratamientos, horarios y
              preguntas habituales.
            </p>
            <ul>
              <li>
                Demostración por teléfono. WhatsApp, próximamente: hoy todavía
                no se puede probar.
              </li>
              <li>Sin compromiso y adaptada a tu clínica.</li>
              <li>Te contactaremos para acordar el mejor momento.</li>
            </ul>
          </div>
          <div className="demo-form-card" data-reveal>
            {submitState === 'success' ? (
              <div className="form-success" role="status">
                <span>✓</span>
                <h3>Solicitud recibida.</h3>
                <p>
                  Te llamaremos para preparar una demo adaptada a tu clínica.
                </p>
              </div>
            ) : (
              <>
                <div className="demo-form-heading">
                  <small>Cuéntanos sobre tu clínica</small>
                  <strong>Preparamos la demo para tu caso.</strong>
                </div>
                <ClinicForm
                  onSubmit={handleFormSubmit}
                  isSubmitting={isSubmitting}
                  variant="demo"
                  compact
                  submitLabel="Solicitar una demo"
                />
                {submitState === 'error' && (
                  <p className="form-error" role="alert">
                    No hemos podido enviar la solicitud. Inténtalo de nuevo.
                  </p>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <footer className="clinic-footer">
        <BrandLogo inverse />
        <p>Recepción inteligente para clínicas dentales.</p>
        <div>
          <a href="#como-funciona">Cómo funciona</a>
          <a href="#menos-ausencias">Recordatorios</a>
          <a href="#demo">Solicitar demo</a>
          <a href={LOGIN_URL}>Acceder</a>
          <a href={REGISTER_URL}>Crear cuenta</a>
        </div>
        <div className="clinic-footer__legal">
          <a href="/aviso-legal">Aviso legal</a>
          <a href="/terminos">Términos y condiciones</a>
          <a href="/politica-privacidad">Política de privacidad</a>
          <a href="/cookies">Política de cookies</a>
          <button type="button" data-cc="show-preferencesModal">
            Configurar cookies
          </button>
        </div>
        <small>© {new Date().getFullYear()} CitasClinicas.AI</small>
      </footer>

      <a
        href={REGISTER_URL}
        className={`sticky-demo${hideStickyCta ? ' is-hidden' : ''}`}
        onClick={() => handleCta('sticky-mobile-alta')}
      >
        Crear mi cuenta
        <span>↗</span>
      </a>
    </div>
  );
};

export default LandingPage;
